import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormField } from '../models/form.model';
import { FormValidationService } from '../service/form-validation.service';

@Component({
  selector: 'app-form-select-multiple-field',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="form-group">
      <label class="form-label">
        {{ field.label }}
        <span *ngIf="field.required" class="required">*</span>
      </label>
      <div *ngIf="field.helpText" class="help-text">{{ field.helpText }}</div>
      <div class="checkbox-group">
        <div *ngFor="let opt of field.options" class="form-check">
          <input
            type="checkbox"
            [id]="field.id + '_' + opt.value"
            class="form-check-input"
            [checked]="isChecked(opt.value)"
            (change)="onCheck($event, opt.value)"
          />
          <label [for]="field.id + '_' + opt.value" class="form-check-label">
            {{ opt.label }}
          </label>
        </div>
      </div>
      <div *ngIf="hasError && error" class="invalid-feedback">
        {{ validationService.getErrorMessage(field, error) }}
      </div>
    </div>
  `,
  styles: [`
    .form-group { margin-bottom: 1rem; }
    .form-label { font-weight: 500; display: block; margin-bottom: 0.5rem; }
    .required { color: red; }
    .help-text { font-size: 0.875rem; color: #6c757d; margin-bottom: 0.5rem; }
    .checkbox-group { display: flex; flex-direction: column; gap: 0.5rem; }
    .form-check { display: flex; align-items: center; }
    .form-check-input { margin-right: 0.5rem; }
    .form-check-label { margin: 0; }
    .invalid-feedback { color: #dc3545; font-size: 0.875rem; margin-top: 0.25rem; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormSelectMultipleFieldComponent implements OnInit, OnChanges {
  @Input() field!: FormField;
  @Input() value: string[] = [];
  @Output() valueChange = new EventEmitter<string[]>();
  @Output() errorChange = new EventEmitter<any>();

  error: any = null;
  hasError = false;

  constructor(public validationService: FormValidationService) {}

  ngOnInit(): void {
    if (!this.value) this.value = [];
    this.validateField();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && !changes['value'].firstChange) {
      this.validateField();
    }
  }

  isChecked(optionValue: string): boolean {
    return this.value && this.value.includes(optionValue);
  }

  onCheck(event: Event, optionValue: string): void {
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      this.value = [...(this.value || []), optionValue];
    } else {
      this.value = this.value.filter(v => v !== optionValue);
    }
    this.valueChange.emit(this.value);
    this.validateField();
  }

  private validateField(): void {
    this.error = this.validationService.validateField(this.field, this.value);
    this.hasError = !!this.error;
    this.errorChange.emit(this.error);
  }
}
